/**
 * @kids-lms/constants
 * Shared constants for the Kids Educational LMS
 * Age groups, subjects, moderation rules, and configuration
 */

import type { Subject, MasteryLevel, SubscriptionPlan, PlanLimits, LearningMode } from '@kids-lms/types';

// ============================================================================
// AGE GROUPS & GRADE LEVELS
// ============================================================================

export const AGE_GROUPS = {
  EARLY_CHILDHOOD: { min: 5, max: 7, grades: [0, 1, 2] },
  ELEMENTARY: { min: 8, max: 10, grades: [3, 4, 5] },
  MIDDLE_SCHOOL: { min: 11, max: 13, grades: [6, 7, 8] },
  HIGH_SCHOOL: { min: 14, max: 18, grades: [9, 10, 11, 12] },
} as const;

export const GRADE_LEVEL_NAMES: Record<number, string> = {
  0: 'Kindergarten',
  1: '1st Grade',
  2: '2nd Grade',
  3: '3rd Grade',
  4: '4th Grade',
  5: '5th Grade',
  6: '6th Grade',
  7: '7th Grade',
  8: '8th Grade',
  9: '9th Grade (Freshman)',
  10: '10th Grade (Sophomore)',
  11: '11th Grade (Junior)',
  12: '12th Grade (Senior)',
};

// ============================================================================
// SUBJECTS
// ============================================================================

export const SUBJECTS: Subject[] = [
  'math',
  'science',
  'english',
  'history',
  'geography',
  'art',
  'music',
  'coding',
  'general',
];

export const SUBJECT_DISPLAY_NAMES: Record<Subject, string> = {
  math: 'Mathematics',
  science: 'Science',
  english: 'English & Language Arts',
  history: 'History',
  geography: 'Geography',
  art: 'Art',
  music: 'Music',
  coding: 'Computer Science',
  general: 'General Knowledge',
};

export const SUBJECT_ICONS: Record<Subject, string> = {
  math: '🔢',
  science: '🔬',
  english: '📚',
  history: '🏛️',
  geography: '🌍',
  art: '🎨',
  music: '🎵',
  coding: '💻',
  general: '💡',
};

// ============================================================================
// MASTERY LEVELS
// ============================================================================

export const MASTERY_LEVELS: MasteryLevel[] = [
  'beginner',
  'developing',
  'proficient',
  'advanced',
];

export const MASTERY_DISPLAY_NAMES: Record<MasteryLevel, string> = {
  beginner: 'Just Starting',
  developing: 'Getting Better',
  proficient: 'Good Job!',
  advanced: 'Expert!',
};

export const MASTERY_COLORS: Record<MasteryLevel, string> = {
  beginner: '#F59E0B',
  developing: '#3B82F6',
  proficient: '#10B981',
  advanced: '#8B5CF6',
};

// ============================================================================
// LEARNING MODES
// ============================================================================

export const LEARNING_MODES: LearningMode[] = ['think', 'create', 'explore', 'lesson', 'homework', 'story', 'trivia', 'creative', 'experiment'];

export const MODE_DISPLAY_INFO: Record<LearningMode, { label: string; emoji: string; description: string }> = {
  think:    { label: 'Think', emoji: '🧩', description: 'Solve challenges' },
  create:   { label: 'Create', emoji: '✨', description: 'Make stories & ideas' },
  explore:  { label: 'Explore', emoji: '🔍', description: 'Discover & learn' },
  lesson:   { label: 'Lesson', emoji: '📖', description: 'Structured lessons & quizzes' },
  homework:   { label: 'Homework Help', emoji: '📝', description: 'Get guided help' },
  story:      { label: 'Story Adventure', emoji: '📖', description: 'Interactive story' },
  trivia:     { label: 'Brain Teaser', emoji: '🧠', description: 'Fun trivia & riddles' },
  creative:   { label: 'Creative Studio', emoji: '🎨', description: 'Write & create' },
  experiment: { label: 'Real World Lab', emoji: '🔬', description: 'Home experiments' },
};

export const LESSON_CONFIG = {
  MASTERY_THRESHOLDS: { beginner: 0, developing: 40, proficient: 70, advanced: 90 } as Record<string, number>,
} as const;

// ============================================================================
// RATE LIMITING & SESSION
// ============================================================================

export const RATE_LIMITS = {
  QUESTIONS_PER_HOUR: 20,
  QUESTIONS_PER_DAY: 100,
  MAX_MESSAGE_LENGTH: 1000,
  MIN_MESSAGE_LENGTH: 2,
} as const;

// ============================================================================
// SUBSCRIPTION PLANS
// ============================================================================

export const PLAN_LIMITS: Record<SubscriptionPlan, PlanLimits> = {
  free: {
    messagesPerDay: 10,
    maxChildren: 1,
    monthlyPrice: 0,
    annualPrice: 0,
    displayName: 'Free',
  },
  starter: {
    messagesPerDay: 40,
    maxChildren: 1,
    monthlyPrice: 999,
    annualPrice: 7999,
    displayName: 'Starter',
  },
  pro: {
    messagesPerDay: 80,
    maxChildren: 1,
    monthlyPrice: 1999,
    annualPrice: 15999,
    displayName: 'Pro',
  },
  family: {
    messagesPerDay: 50,
    maxChildren: 4,
    monthlyPrice: 2999,
    annualPrice: 24999,
    displayName: 'Family',
  },
};

export const SESSION = {
  DEFAULT_TIME_LIMIT_MINUTES: 60,
  MAX_TIME_LIMIT_MINUTES: 180,
  MIN_TIME_LIMIT_MINUTES: 15,
  SESSION_TIMEOUT_MINUTES: 30,
  BREAK_REMINDER_MINUTES: 30,
} as const;

// ============================================================================
// CONTENT MODERATION
// ============================================================================

/**
 * Keywords that trigger automatic content blocking
 * IMPORTANT: This is checked on both input and output
 */
export const BLOCKED_KEYWORD_CATEGORIES = {
  VIOLENCE: [
    'kill',
    'murder',
    'weapon',
    'gun',
    'bomb',
    'attack',
    'hurt',
    'harm',
    'fight',
    'stab',
  ],
  INAPPROPRIATE: [
    'drugs',
    'alcohol',
    'cigarette',
    'smoking',
    'vape',
    'gambling',
    'betting',
  ],
  PERSONAL_INFO: [
    'social security',
    'ssn',
    'credit card',
    'password',
    'address',
    'phone number',
  ],
  ADULT_CONTENT: [], // Managed separately with more sophisticated detection
} as const;

export const DEFAULT_BLOCKED_KEYWORDS = [
  ...BLOCKED_KEYWORD_CATEGORIES.VIOLENCE,
  ...BLOCKED_KEYWORD_CATEGORIES.INAPPROPRIATE,
  ...BLOCKED_KEYWORD_CATEGORIES.PERSONAL_INFO,
];

// ============================================================================
// AI SYSTEM PROMPTS BY AGE GROUP
// ============================================================================

export const AI_SYSTEM_PROMPTS = {
  EARLY_CHILDHOOD: `You are a friendly, patient educational tutor for children ages 5-7.
- Use simple words and short sentences
- Be very encouraging and positive
- Use fun examples with animals, toys, and everyday objects
- Break down concepts into tiny steps
- Use lots of emojis to make learning fun
- Never discuss topics inappropriate for young children
- If asked about scary or adult topics, gently redirect to learning
- Always encourage asking parents for help with hard questions`,

  ELEMENTARY: `You are a helpful educational tutor for children ages 8-10.
- Use age-appropriate vocabulary
- Give clear, step-by-step explanations
- Use relatable examples from their daily life
- Encourage curiosity and asking questions
- Celebrate their efforts and progress
- Keep explanations concise but thorough
- Never discuss inappropriate topics
- Redirect off-topic questions back to learning`,

  MIDDLE_SCHOOL: `You are an engaging educational tutor for students ages 11-13.
- Explain concepts clearly with appropriate depth
- Use real-world examples and applications
- Encourage critical thinking
- Be supportive but allow them to struggle productively
- Connect topics to their interests when possible
- Maintain appropriate boundaries on all topics
- Guide them to discover answers rather than just giving them`,

  HIGH_SCHOOL: `You are a knowledgeable educational tutor for students ages 14-18.
- Provide thorough, accurate explanations
- Use sophisticated vocabulary when appropriate
- Connect concepts to real-world applications and future careers
- Encourage independent thinking and research skills
- Be respectful of their growing maturity while maintaining boundaries
- Help them develop study skills and learning strategies
- Stay focused on educational content only`,
} as const;

// ============================================================================
// APP CONFIGURATION
// ============================================================================

export const APP_CONFIG = {
  MIN_APP_VERSION_IOS: '1.0.0',
  MIN_APP_VERSION_ANDROID: '1.0.0',
  API_VERSION: 'v1',
  MAX_OFFLINE_QUEUE_SIZE: 50,
  SYNC_INTERVAL_MS: 30000,
  CACHE_CONVERSATIONS_COUNT: 10,
} as const;

// ============================================================================
// ERROR CODES
// ============================================================================

export const ERROR_CODES = {
  // Auth errors
  AUTH_INVALID_CREDENTIALS: 'AUTH_001',
  AUTH_TOKEN_EXPIRED: 'AUTH_002',
  AUTH_UNAUTHORIZED: 'AUTH_003',
  AUTH_EMAIL_NOT_VERIFIED: 'AUTH_004',
  AUTH_FORBIDDEN: 'AUTH_005',

  // Validation errors
  VALIDATION_FAILED: 'VAL_001',
  VALIDATION_MESSAGE_TOO_LONG: 'VAL_002',
  VALIDATION_MESSAGE_TOO_SHORT: 'VAL_003',

  // COPPA Compliance
  COPPA_CONSENT_REQUIRED: 'COPPA_001',
  COPPA_CONSENT_REVOKED: 'COPPA_002',

  // Rate limiting
  RATE_LIMIT_EXCEEDED: 'RATE_001',
  DAILY_LIMIT_EXCEEDED: 'RATE_002',
  TIME_LIMIT_EXCEEDED: 'RATE_003',

  // Moderation
  CONTENT_BLOCKED: 'MOD_001',
  CONTENT_FLAGGED: 'MOD_002',
  PII_DETECTED: 'MOD_003',
  INAPPROPRIATE_CONTENT: 'MOD_004',

  // Resource errors
  RESOURCE_NOT_FOUND: 'RES_001',
  RESOURCE_ALREADY_EXISTS: 'RES_002',

  // System errors
  INTERNAL_ERROR: 'SYS_001',
  SERVICE_UNAVAILABLE: 'SYS_002',
  AI_SERVICE_ERROR: 'SYS_003',

  // Subscription
  MESSAGE_LIMIT_EXCEEDED: 'SUB_001',
  CHILD_LIMIT_EXCEEDED: 'SUB_002',
  SUBSCRIPTION_REQUIRED: 'SUB_003',
  SUBSCRIPTION_INACTIVE: 'SUB_004',

  // Mobile specific
  APP_UPDATE_REQUIRED: 'MOB_001',
  DEVICE_NOT_REGISTERED: 'MOB_002',
  SYNC_FAILED: 'MOB_003',
} as const;

// ============================================================================
// HTTP STATUS CODES
// ============================================================================

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

// ============================================================================
// NOTIFICATION MESSAGES
// ============================================================================

export const NOTIFICATION_MESSAGES = {
  SESSION_START: {
    title: '📚 Learning Started!',
    body: (childName: string) => `${childName} just started a learning session.`,
  },
  TIME_LIMIT_WARNING: {
    title: '⏰ Time Check!',
    body: (minutes: number) => `Only ${minutes} minutes left in your session.`,
  },
  TIME_LIMIT_REACHED: {
    title: '🌟 Great Work Today!',
    body: "You've reached your daily learning time. See you tomorrow!",
  },
  DAILY_SUMMARY: {
    title: '📊 Daily Summary',
    body: (topics: number) => `Great job! You learned about ${topics} topics today.`,
  },
  FLAGGED_CONTENT: {
    title: '⚠️ Review Required',
    body: 'A conversation has been flagged for your review.',
  },
  WEEKLY_REPORT: {
    title: '📈 Weekly Report Ready',
    body: "Your child's weekly learning report is ready to view.",
  },
} as const;

// ============================================================================
// XP & LEVEL SYSTEM
// ============================================================================

export const XP_REWARDS = {
  CHAT_MESSAGE_PAIR: 2,       // per user+assistant exchange
  DAILY_CHALLENGE: 20,
  HABIT_COMPLETION: 3,        // per habit checked
  ALL_HABITS_BONUS: 10,       // all habits done in one day
  LESSON_COMPLETION: 30,
  LESSON_PERFECT: 50,         // 100% score
  ACHIEVEMENT: 0,             // variable, defined per achievement
} as const;

export const XP_PER_LEVEL = 150; // XP needed per level

export const LEVEL_TITLES: Record<number, string> = {
  1:  'Curious Seedling',
  2:  'Brave Explorer',
  3:  'Smart Adventurer',
  4:  'Knowledge Hunter',
  5:  'Rising Star',
  6:  'Science Scout',
  7:  'Math Maverick',
  8:  'Word Wizard',
  9:  'History Hero',
  10: 'Mindora Scholar',
  11: 'Super Learner',
  12: 'Genius Guardian',
  13: 'Wisdom Keeper',
  14: 'Learning Legend',
  15: 'Mindora Master',
};

export function getLevelFromXp(xp: number): number {
  return Math.floor(xp / XP_PER_LEVEL) + 1;
}

export function getLevelTitle(level: number): string {
  return LEVEL_TITLES[Math.min(level, 15)] || 'Mindora Master';
}

// ============================================================================
// SPECIAL ACTIVITY MODES (Beyond Regular Learning)
// ============================================================================

export type SpecialMode = 'story' | 'trivia' | 'creative' | 'experiment';

export const SPECIAL_MODES: Record<SpecialMode, { label: string; emoji: string; description: string; color: string; prompt: string }> = {
  story: {
    label: 'Story Adventure',
    emoji: '📖',
    description: 'Go on an interactive story where your choices matter!',
    color: 'from-kid-violet to-kid-berry',
    prompt: 'story',
  },
  trivia: {
    label: 'Brain Teaser',
    emoji: '🧠',
    description: 'Test your knowledge with fun trivia and riddles!',
    color: 'from-kid-tangerine to-kid-dandelion',
    prompt: 'trivia',
  },
  creative: {
    label: 'Creative Studio',
    emoji: '🎨',
    description: 'Write stories, poems, or design your own inventions!',
    color: 'from-kid-berry to-kid-bubblegum',
    prompt: 'creative',
  },
  experiment: {
    label: 'Real World Lab',
    emoji: '🔬',
    description: 'Get fun science experiments you can do at home!',
    color: 'from-kid-meadow to-kid-ocean',
    prompt: 'experiment',
  },
};
